// App 內的提示訊息（toast）與確認對話框，取代瀏覽器內建的 alert／confirm
// toast：畫面下方顯示，幾秒後自動消失，不擋住操作；confirmDialog：回傳 Promise<boolean>
import React, { useEffect, useState } from 'react';

type Toast = { id: number; text: string };
type Ask = { text: string; okText: string; danger: boolean; resolve: (ok: boolean) => void; input?: { placeholder: string; resolveText: (v: string | null) => void } };

let pushToast: (t: Toast) => void = () => {};
let pushAsk: (a: Ask) => void = a => a.resolve(window.confirm(a.text));   // 元件尚未掛載時退回內建對話框
let seq = 0;

export function toast(text: unknown) {
  pushToast({ id: ++seq, text: String(text) });
}

export function confirmDialog(text: string, opts: { okText?: string; danger?: boolean } = {}): Promise<boolean> {
  return new Promise(resolve => pushAsk({ text, okText: opts.okText ?? '確定', danger: !!opts.danger, resolve }));
}

// 文字輸入對話框：取代瀏覽器的 prompt()（iPhone 主畫面 App 與部分瀏覽器會直接擋掉 prompt）；取消回傳 null
export function promptDialog(text: string, opts: { placeholder?: string; okText?: string } = {}): Promise<string | null> {
  return new Promise(resolveText => pushAsk({ text, okText: opts.okText ?? '送出', danger: false, resolve: () => {}, input: { placeholder: opts.placeholder ?? '', resolveText } }));
}

// 顯示時間依字數調整：短訊息 2.5 秒，長訊息最多 8 秒
const lifeOf = (text: string) => Math.min(8000, 2500 + text.length * 60);

export function FeedbackHost() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    pushToast = t => {
      setToasts(list => [...list.slice(-2), t]);   // 最多同時顯示 3 則
      window.setTimeout(() => setToasts(list => list.filter(x => x.id !== t.id)), lifeOf(t.text));
    };
    pushAsk = a => { setDraft(''); setAsk(a); };
    return () => { pushToast = () => {}; pushAsk = a => a.resolve(window.confirm(a.text)); };
  }, []);

  // 確認對話框：Esc 取消、Enter 確定
  useEffect(() => {
    if (!ask) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { ask.resolve(false); ask.input?.resolveText(null); setAsk(null); }
      if (e.key === 'Enter' && !ask.input) { ask.resolve(true); setAsk(null); }   // 輸入框內 Enter 是換行，不當作送出
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ask]);

  const answer = (ok: boolean) => {
    ask?.resolve(ok);
    ask?.input?.resolveText(ok ? draft.trim() || null : null);
    setAsk(null);
  };
  const btn: React.CSSProperties = { flex: 1, padding: '11px 8px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };

  return (
    <>
      <div aria-live="polite" style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(20px + env(safe-area-inset-bottom))', zIndex: 10001,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, pointerEvents: 'none', padding: '0 16px' }}>
        {toasts.map(t => (
          <div key={t.id} role="status" className="toast-in" onClick={() => setToasts(list => list.filter(x => x.id !== t.id))}
            style={{ pointerEvents: 'auto', maxWidth: 440, background: 'rgba(30,41,59,0.96)', color: '#fff', borderRadius: 12, padding: '11px 16px',
              fontSize: 14, lineHeight: 1.5, boxShadow: '0 6px 24px rgba(0,0,0,.25)', whiteSpace: 'pre-line', cursor: 'pointer' }}>
            {t.text}
          </div>
        ))}
      </div>
      {ask && (
        <div role="dialog" aria-modal="true" onClick={e => { if (e.target === e.currentTarget) answer(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="toast-in" style={{ background: 'var(--card, #fff)', color: 'var(--text, #1e293b)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 360, boxShadow: '0 12px 40px rgba(0,0,0,.3)' }}>
            <p style={{ margin: ask.input ? '0 0 10px' : '0 0 18px', fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{ask.text}</p>
            {ask.input && (
              <textarea value={draft} onChange={e => setDraft(e.target.value)} placeholder={ask.input.placeholder} rows={3} autoFocus maxLength={300}
                style={{ width: '100%', boxSizing: 'border-box', margin: '0 0 14px', padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--border, #e2e8f0)',
                  background: 'var(--bg2, #f1f5f9)', color: 'var(--text, #1e293b)', fontSize: 16, fontFamily: 'inherit', resize: 'none' }} />
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => answer(false)} style={{ ...btn, border: '1px solid var(--border, #e2e8f0)', background: 'var(--bg2, #f1f5f9)', color: 'var(--text, #1e293b)' }}>取消</button>
              <button onClick={() => answer(true)} autoFocus={!ask.input} style={{ ...btn, border: 'none', background: ask.danger ? '#dc2626' : 'var(--accent, #2563eb)', color: '#fff' }}>{ask.okText}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
